import { ElementType } from 'react';

import { styled, SvgIcon as MuiSvgIcon, SvgIconProps as MuiSvgIconProps, Theme } from '@mui/material';

import { split } from '../../utils/string';

type NamedColor = keyof Pick<Theme['palette'], 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'grey'>;
type ColorVariant = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

type BackgroundColors = keyof Theme['palette']['background'];
type CommonColors = keyof Theme['palette']['common'];

type Color =
  | NamedColor
  | `${NamedColor}.${ColorVariant}`
  | `background.${BackgroundColors}`
  | `common.${CommonColors}`
  | 'inherit';

export type SvgIconProps<RootComponent extends ElementType = 'svg', AdditionalProps = {}> = DistributiveOmit<
  MuiSvgIconProps<RootComponent, AdditionalProps>,
  'fontSize' | 'color'
> & {
  /** @default 16 */
  size?: number;
  /** @default 'inherit' */
  color?: Color;
};

const StyledSvgIcon = styled(({ color, ...props }: SvgIconProps) => <MuiSvgIcon {...props} />)<{
  size?: number;
  color?: Color;
}>(({ theme, size = 16, color = 'inherit' }) => {
  let _color: string | undefined = undefined;

  if (color === 'inherit') {
    _color = 'inherit';
  } else {
    const [colorName, intensity] = split(color, '.');
    switch (colorName) {
      case 'background':
        _color = theme.palette[colorName][intensity === 'contrastColors' ? 'default' : intensity];
        break;
      case 'common':
        _color = theme.palette[colorName][intensity];
        break;
      default:
        _color = theme.palette[colorName][intensity ?? 500];
        break;
    }
  }

  return {
    width: theme.typography.pxToRem(size),
    height: theme.typography.pxToRem(size),
    color: _color,
  };
});

const SvgIcon = (props: SvgIconProps) => {
  return <StyledSvgIcon {...props} />;
};

export default SvgIcon;
